import { useEffect, useRef } from 'react';
import useFocusedViewTracker from '@/hooks/content/useFocusedViewTracker';
import { getDurationSeconds } from '@/modules/content/loop/model/contentDetails';
import { TContent } from '@/types/Content.types';

// Drives the view tracker from the loop's current item: blur the item we left, focus the
// one we arrived at, and pause while the tab is hidden — time on another tab is not time
// spent consuming.
//
// This is the only place a view can be reported. Rendering or prefetching an item emits
// nothing, and for video the tracker additionally waits for real playback.
const useFocusedDwell = (current: TContent | undefined) => {
  const { onItemFocused, onItemBlurred, onPlaybackStateChange, onPlaybackProgress } = useFocusedViewTracker();
  const previousRef = useRef<TContent | undefined>(undefined);

  useEffect(() => {
    const previous = previousRef.current;
    if (previous && previous.id !== current?.id) onItemBlurred(previous);

    if (current) onItemFocused(current, getDurationSeconds(current));
    previousRef.current = current;
  }, [current, onItemFocused, onItemBlurred]);

  useEffect(() => {
    const onVisibilityChange = () => {
      const item = previousRef.current;
      if (!item) return;
      if (document.hidden) onItemBlurred(item);
      else onItemFocused(item, getDurationSeconds(item));
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [onItemFocused, onItemBlurred]);

  return { onPlaybackStateChange, onPlaybackProgress };
};

export default useFocusedDwell;
